import React, {
  type ChangeEvent,
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibCheckbox, KibRadio } from '@chewy/kib-choices-react'
import { KibField, KibSelect, KibTextarea } from '@chewy/kib-fields-new-react'
import { KibInputGroup } from '@chewy/kib-fields-react'
import { Tooltip } from '@mui/material'
import classNames from 'classnames'
import { type Field, Utils } from '@pbt/pbt-ui-components'

import UserSelect from '~/components/common/inputs/UserSelect'
import useTeamMembers from '~/components/common/inputs/useTeamMembers'
import DoctorLicenseStateWarning from '~/components/dashboard/prescription/prescription-editor/DoctorLicenseStateWarning'
import DoctorLicenseWarning from '~/components/dashboard/prescription/prescription-editor/DoctorLicenseWarning'
import PrescriptionChewyOrderInfo from '~/components/dashboard/prescription/prescription-editor/PrescriptionChewyOrderInfo'
import QuantityAndRefills from '~/components/dashboard/prescription/prescription-editor/QuantityAndRefills'
import SwapProductButtons from '~/components/dashboard/prescription/prescription-editor/SwapProductButtons'
import { TaskFormValidationErrorToast } from '~/components/dashboard/tasks-dashboard/TaskFormValidationErrorToast'
import FeatureToggle from '~/constants/featureToggle'
import {
  AdministrationCourseType,
  AdministrationCourseTypeStr,
  PrescriptionType,
} from '~/constants/prescription'
import RoleName from '~/constants/roleNames'
import { fetchMembersForSelectList } from '~/store/actions/members'
import { usePrescriptionAdminInstructions } from '~/store/hooks/orders'
import { useMainStaffRoles } from '~/store/hooks/useMainStaffRoles'
import {
  getDrugAdministrationFrequencies,
  getDrugDeliveryMethod,
  getFeatureToggle,
  getPrescriptionProductSizeUnit,
} from '~/store/reducers/constants'
import { getMembersSelectList } from '~/store/reducers/members'
import type { DataHandle, Prescription } from '~/types'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import type { RefillableSectionDataHandle } from '../RefillableSection'
import SwapProductRefill, {
  type SwapProductRefillHandle,
} from './SwapProductRefill'
import SwapProductTaskDetails from './SwapProductTaskDetails'

import style from './SwapProductReactiveForm.module.scss'

const CUSTOM = 'CUSTOM'
const AUTOFILL = 'AUTOFILL'
const MAX_TEAM_MEMBERS = 1000

export interface SwapProductReactiveFormProps {
  clearApproveValidationError?: () => void
  currentDoctorHasDvmLicense: boolean | null
  currentDoctorHasMatchingStateLicense: boolean | null
  fields?: {
    automaticallyCreateTask?: Field
    doctorId?: Field
    teamMemberId?: Field
  }
  isVetDiet: boolean
  onRefChange?: () => void
  prescription: Prescription
  refillableSectionRef?: RefObject<
    RefillableSectionDataHandle | SwapProductRefillHandle
  >
  showAutomaticallyCreateTaskOption?: boolean
  taskSectionRef?: RefObject<DataHandle>
}

export interface SwapProductReactiveFormHandle extends DataHandle {}

const SwapProductReactiveForm = forwardRef<
  SwapProductReactiveFormHandle,
  SwapProductReactiveFormProps
>(function SwapProductReactiveForm(
  {
    currentDoctorHasDvmLicense,
    currentDoctorHasMatchingStateLicense,
    fields,
    isVetDiet,
    onRefChange,
    prescription,
    refillableSectionRef,
    clearApproveValidationError,
    showAutomaticallyCreateTaskOption,
    taskSectionRef,
  },
  ref,
) {
  const [showSwapValidationError, setShowSwapValidationError] = useState(false)

  const dispatch = useDispatch()
  const { t } = useTranslation(['Common', 'Dialogs', 'Tooltips', 'Tasks'])

  const PrescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  )
  const DrugDeliveryMethod = useSelector(getDrugDeliveryMethod)
  const DrugAdministrationFrequencies = useSelector(
    getDrugAdministrationFrequencies,
  )
  const dvmList = useSelector(getMembersSelectList)
  const teamMembers = useTeamMembers(dvmList)
  const RolesList = useMainStaffRoles()
  const isLicenseStateValidationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FE_DVM_LICENSE_STATE_VALIDATION),
  )

  const isDvmLicenseRequired =
    prescription.prescriptionType === PrescriptionType.CHEWY

  const [quantity, setQuantity] = useState<number | undefined>(
    prescription.administrationQuantity ?? undefined,
  )
  const [unit, setUnit] = useState(
    prescription.administrationQuantityUnitId || '',
  )
  const [route, setRoute] = useState(prescription.administrationRouteId || '')
  const [frequency, setFrequency] = useState(
    prescription.administrationFrequencyId || '',
  )
  const [lengthOfCourse, setLengthOfCourse] = useState<
    AdministrationCourseType | undefined
  >(prescription.administrationCourseType || AdministrationCourseType.CUSTOM)
  const [days, setDays] = useState<number | undefined>(
    prescription.administrationCourseLength ?? undefined,
  )
  const [instructionsType, setInstructionsType] = useState<string>(
    prescription.administrationInstructionsAutofill ? AUTOFILL : CUSTOM,
  )
  const [instructionsText, setInstructionsText] = useState<string>(
    prescription.notes || '',
  )

  const autofilledInstructions = usePrescriptionAdminInstructions({
    administrationQuantity: quantity,
    administrationRouteId: route,
    administrationFrequencyId: frequency,
    administrationQuantityUnitId: unit,
    administrationCourseLength: days,
    administrationCourseType: lengthOfCourse,
    variation: prescription.variation,
  })

  useEffect(() => {
    if (instructionsType === AUTOFILL) {
      setInstructionsText(autofilledInstructions)
    }
  }, [instructionsType, autofilledInstructions])

  const prescriptionId = prescription.id
  const variationId =
    prescription.variationId || prescription.globalVariationId || ''

  useEffectExceptOnMount(() => {
    setQuantity(prescription.administrationQuantity ?? undefined)
    setUnit(prescription.administrationQuantityUnitId || '')
    setRoute(prescription.administrationRouteId || '')
    setFrequency(prescription.administrationFrequencyId || '')
    setLengthOfCourse(
      prescription.administrationCourseType || AdministrationCourseType.CUSTOM,
    )
    setDays(prescription.administrationCourseLength ?? undefined)
    setInstructionsType(
      prescription.administrationInstructionsAutofill ? AUTOFILL : CUSTOM,
    )
    setInstructionsText(prescription.notes || '')
  }, [prescriptionId, variationId])

  const handleTaskChange = useCallback(() => {
    onRefChange?.()
  }, [onRefChange])

  const handleRefillChange = useCallback(() => {
    onRefChange?.()
  }, [onRefChange])

  const rolesListRef = useRef(RolesList)
  rolesListRef.current = RolesList

  useEffect(() => {
    const roleIds = [RoleName.Veterinarian, RoleName.Veterinarian2].flatMap(
      (roleName) => Utils.findConstantIdsByName(roleName, rolesListRef.current),
    )
    dispatch(
      fetchMembersForSelectList({
        from: 0,
        to: MAX_TEAM_MEMBERS,
        includeInactive: false,
        shorten: true,
        roleIds,
        noRolesInResult: true,
      }),
    )
  }, [dispatch])

  // Single source of truth for collecting form data
  const getAdministrationFields = useCallback(
    () => ({
      administrationQuantity: quantity,
      administrationQuantityUnitId: unit,
      administrationRouteId: route,
      administrationFrequencyId: frequency,
      administrationCourseType: lengthOfCourse,
      administrationCourseLength:
        lengthOfCourse === AdministrationCourseType.CUSTOM ? days : undefined,
      notes: instructionsText,
      administrationInstructionsAutofill:
        instructionsType === AUTOFILL && Boolean(instructionsText),
    }),
    [
      quantity,
      unit,
      route,
      frequency,
      lengthOfCourse,
      days,
      instructionsText,
      instructionsType,
    ],
  )

  const currentPrescription: Prescription = {
    ...prescription,
    ...getAdministrationFields(),
    doctorId: fields?.doctorId?.value || prescription.doctorId,
    ...refillableSectionRef?.current?.get(),
    ...(fields?.automaticallyCreateTask?.value &&
    taskSectionRef?.current?.validate()
      ? {
          clientTasks: taskSectionRef.current.get(),
        }
      : {}),
  } as Prescription

  const validateAdminFields = () => {
    if (!quantity || quantity < 1) return false
    if (!unit) return false
    if (!route) return false
    if (!frequency) return false
    if (lengthOfCourse === AdministrationCourseType.CUSTOM && !days) {
      return false
    }
    return true
  }

  const validateRefillSection = () => {
    if (!refillableSectionRef?.current) return true
    return refillableSectionRef.current.validate()
  }

  const validateTaskSection = () => {
    if (!fields?.automaticallyCreateTask?.value) return true
    if (!taskSectionRef?.current) return true
    return taskSectionRef.current.validate()
  }

  const handleSwapValidation = useCallback(() => {
    const doctorValid = Boolean(fields?.doctorId?.value)
    setShowSwapValidationError(!doctorValid)
    return doctorValid
  }, [fields?.doctorId])

  useImperativeHandle(
    ref,
    () => ({
      get: getAdministrationFields,
      validate: () => {
        if (!fields?.doctorId) return false
        if (!fields.doctorId.value) return false

        if (isVetDiet && !instructionsText) return false
        if (!isVetDiet && !validateAdminFields()) return false
        if (!validateRefillSection()) return false
        if (!validateTaskSection()) return false
        return true
      },
    }),
    [getAdministrationFields, fields, isVetDiet, instructionsText],
  )

  return (
    <div className={style.prescriptionBody}>
      <div className={style.topSection}>
        <PrescriptionChewyOrderInfo displayAsRow prescription={prescription} />
        <QuantityAndRefills isVetDiet={isVetDiet} prescription={prescription} />
        <SwapProductButtons
          prescription={currentPrescription}
          onBeforeSwap={handleSwapValidation}
        />
        {(clearApproveValidationError || showSwapValidationError) && (
          <TaskFormValidationErrorToast
            onClose={
              clearApproveValidationError ||
              (() => setShowSwapValidationError(false))
            }
          />
        )}
      </div>
      <div className={style.adminSection}>
        {!isVetDiet && (
          <>
            <div className={style.adminSectionHeader}>
              {t('Dialogs:PRESCRIPTION_DIALOG:ADMINISTRATION_INSTRUCTIONS')}
            </div>
            <div className={style.adminSectionSplitItems}>
              <KibField
                label={t('Common:INSTRUCTION_QUANTITY')}
                min="1"
                placeholder={t('Common:QTY')}
                type="number"
                value={quantity ?? ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const { value } = e.target
                  setQuantity(value === '' ? undefined : parseInt(value, 10))
                }}
              />
              <KibSelect
                className={classNames({ [style.placeholderColor]: !unit })}
                label={t('Common:UNIT')}
                value={unit}
                onChange={(e) => {
                  setUnit(e.target.value)
                }}
              >
                <option disabled hidden value="">
                  {t('Common:UNIT')}
                </option>
                {PrescriptionProductSizeUnit.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nameTranslation}
                  </option>
                ))}
              </KibSelect>
              <KibSelect
                className={classNames({ [style.placeholderColor]: !route })}
                label={t('Common:ROUTE_OF_DRUG_ADMINISTRATION')}
                value={route}
                onChange={(e) => {
                  setRoute(e.target.value)
                }}
              >
                <option disabled hidden value="">
                  {t('Common:ROUTE_OF_DRUG_ADMINISTRATION')}
                </option>
                {DrugDeliveryMethod.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nameTranslation}
                  </option>
                ))}
              </KibSelect>
              <KibSelect
                className={classNames({ [style.placeholderColor]: !frequency })}
                label={t('Common:FREQUENCY_ONE')}
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              >
                <option disabled hidden value="">
                  {t('Common:FREQUENCY_ONE')}
                </option>
                {DrugAdministrationFrequencies.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nameTranslation}
                  </option>
                ))}
              </KibSelect>
            </div>
            <div className={style.adminLengthOfCourse}>
              <KibInputGroup
                label={t('Dialogs:PRESCRIPTION_DIALOG.PERIOD_OF_TIME')}
              >
                <div className={style.radioChoices}>
                  <KibRadio
                    className={style.dayRadio}
                    label={
                      <span className={style.dayInput}>
                        <KibField
                          hiddenLabel
                          key={lengthOfCourse}
                          label={t('Constants:TIME_UNITS.DAY_OTHER')}
                          required={
                            lengthOfCourse === AdministrationCourseType.CUSTOM
                          }
                          value={days ?? ''}
                          onChange={(e) => {
                            const { value } = e.target
                            setDays(
                              value === '' ? undefined : parseInt(value, 10),
                            )
                          }}
                        />
                        {t('Constants:TIME_UNITS.DAY_OTHER')}
                      </span>
                    }
                    selectedValue={lengthOfCourse}
                    value={AdministrationCourseType.CUSTOM}
                    onChange={() =>
                      setLengthOfCourse(AdministrationCourseType.CUSTOM)
                    }
                  />
                  <KibRadio
                    label={AdministrationCourseTypeStr.UNTIL_GONE}
                    selectedValue={lengthOfCourse}
                    value={AdministrationCourseType.UNTIL_GONE}
                    onChange={() =>
                      setLengthOfCourse(AdministrationCourseType.UNTIL_GONE)
                    }
                  />
                  <KibRadio
                    label={AdministrationCourseTypeStr.AS_NEEDED}
                    selectedValue={lengthOfCourse}
                    value={AdministrationCourseType.AS_NEEDED}
                    onChange={() =>
                      setLengthOfCourse(AdministrationCourseType.AS_NEEDED)
                    }
                  />
                </div>
              </KibInputGroup>
            </div>
          </>
        )}
        <div className={style.administrationInstructions}>
          {isVetDiet ? (
            <KibInputGroup
              className={style.vetDietInstructions}
              label={
                <span className={style.detailsLabel}>
                  {t('Common:DETAILS')}
                  <span className={style.requiredAsterisk}>*</span>
                </span>
              }
            >
              <KibTextarea
                hiddenLabel
                label={t('Common:DETAILS')}
                placeholder={t('Common:INSTRUCTIONS')}
                rows={2}
                value={instructionsText}
                onChange={(e) => setInstructionsText(e.target.value)}
              />
              <div className={style.customHelpText}>
                {t('Dialogs:PRESCRIPTION_DIALOG.NOTES_HELPER_TEXT', {
                  notesLength: instructionsText.length || 0,
                })}
              </div>
            </KibInputGroup>
          ) : (
            <>
              <KibInputGroup label={t('Common:INSTRUCTION_PRINTED_ON_LABEL')}>
                <div className={style.radioChoices}>
                  <KibRadio
                    label={t('Common:WRITE_MY_OWN_PRESCRIPTION')}
                    selectedValue={instructionsType}
                    value={CUSTOM}
                    onChange={() => setInstructionsType(CUSTOM)}
                  />
                  <KibRadio
                    label={t('Dialogs:PRESCRIPTION_DIALOG.AUTOFILL')}
                    selectedValue={instructionsType}
                    value={AUTOFILL}
                    onChange={() => setInstructionsType(AUTOFILL)}
                  />
                </div>
              </KibInputGroup>
              <Tooltip
                enterDelay={300}
                placement="top-end"
                title={
                  instructionsType === AUTOFILL
                    ? t('Tooltips:CLICK_WRITE_MY_OWN_TO_EDIT')
                    : ''
                }
              >
                <>
                  <KibTextarea
                    hiddenLabel
                    label={t('Common:INSTRUCTIONS')}
                    placeholder={
                      instructionsType === AUTOFILL
                        ? ''
                        : t('Common:INSTRUCTIONS')
                    }
                    readOnly={instructionsType === AUTOFILL}
                    rows={instructionsType === AUTOFILL ? 1 : 2}
                    value={instructionsText}
                    onChange={(e) => setInstructionsText(e.target.value)}
                  />
                  {instructionsType === CUSTOM && (
                    <div className={style.customHelpText}>
                      {t('Dialogs:PRESCRIPTION_DIALOG.NOTES_HELPER_TEXT', {
                        notesLength: instructionsText.length || 0,
                      })}
                    </div>
                  )}
                </>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <div className={style.refillSection}>
        <SwapProductRefill
          prescription={prescription}
          ref={refillableSectionRef}
          onRefChange={handleRefillChange}
        />
      </div>

      {fields?.doctorId &&
        (() => {
          const hasDoctorError =
            showSwapValidationError && !fields.doctorId.value
          return (
            <div className={style.selectMembers}>
              <div
                className={classNames({
                  [style.errorSelectWrapper]: hasDoctorError,
                })}
              >
                <KibSelect
                  required
                  className={classNames({
                    [style.placeholderColor]: !fields.doctorId.value,
                  })}
                  label={t('Common:DOCTOR')}
                  value={fields.doctorId.value}
                  onChange={(e) => {
                    fields.doctorId?.setValue(e.target.value)
                    if (e.target.value) {
                      setShowSwapValidationError(false)
                    }
                  }}
                >
                  <option disabled hidden value="">
                    {t('Common:SELECT_A_DOCTOR')}
                  </option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {`${member.firstName} ${member.lastName}`}
                    </option>
                  ))}
                </KibSelect>
              </div>
              {hasDoctorError && (
                <div className={style.doctorError}>
                  {t('Common:SELECT_A_DOCTOR')}
                </div>
              )}
            </div>
          )
        })()}

      {isDvmLicenseRequired && (
        <>
          <DoctorLicenseWarning
            currentDoctorHasDvmLicense={currentDoctorHasDvmLicense}
            doctorId={fields?.doctorId?.value}
          />
          {isLicenseStateValidationEnabled &&
            currentDoctorHasDvmLicense === true && (
              <DoctorLicenseStateWarning
                doctorId={fields?.doctorId?.value}
                hasMatchingStateLicense={currentDoctorHasMatchingStateLicense}
              />
            )}
        </>
      )}

      {fields?.teamMemberId && (
        <div className={style.teamMemberSection}>
          <div className={style.sectionLabel}>
            {t('Tasks:TASK_DETAILS.TASK_DETAILS')}
          </div>
          <div className={style.selectMembers}>
            <UserSelect
              emptyLabel={t('Common:ASSIGN_TEAM_MEMBER')}
              field={fields.teamMemberId}
              label={t('Common:ASSIGN_TEAM_MEMBER')}
            />
          </div>
        </div>
      )}

      {showAutomaticallyCreateTaskOption && fields?.automaticallyCreateTask && (
        <div className={style.taskSection}>
          <KibCheckbox
            checked={fields.automaticallyCreateTask.value}
            label={t('Dialogs:PRESCRIPTION_DIALOG.AUTOMATICALLY_CREATE_TASK')}
            onChange={(checked) =>
              fields.automaticallyCreateTask?.setValue(checked as boolean)
            }
          />
          {fields.automaticallyCreateTask.value && taskSectionRef && (
            <SwapProductTaskDetails
              prescription={prescription}
              ref={taskSectionRef}
              onRefChange={handleTaskChange}
            />
          )}
        </div>
      )}
    </div>
  )
})

export default SwapProductReactiveForm
